import { Trophy, BarChart3, Megaphone, Cog } from "lucide-react";

export interface ExtraSection {
  heading: string;
  description: string;
  videoLabel?: string;
}

export interface Submodule {
  slug: string;
  label: string;
  title: string;
  description: string;
  heroEyebrow?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  heroDescriptions?: string[];
  demoHeading?: string;
  demoDescription?: string;
  extraSections?: ExtraSection[];
  videoUrl?: string;
}

export interface Module {
  slug: string;
  name: string;
  tagline: string;
  icon: React.ElementType;
  submodules: Submodule[];
}

export const MODULES: Module[] = [
  {
    slug: "audience-engagement",
    name: "QUIZ ENGINE",
    tagline: "Explore all of Fanpictor’s gamification features through videos, product walkthroughs, and playable experiences.",
    icon: Trophy,
    submodules: [
      {
        slug: "lightshow",
        label: "Lightshow",
        title: "Lightshow",
        description: "Turn smartphones into synchronized light pixels across the venue, creating an immersive, choreographed light show controlled in real time.",
        heroTitle: "Lightshow",
        heroSubtitle: "by Fanpictor",
        heroDescriptions: [
          "From stadiums and arenas to festivals, conferences, clubs, and branded events, Lightshow creates large-scale shared moments without wearables, wristbands, or additional hardware.",
          "Experiences can be fully customized through colors, transitions, timing, and sound; allowing every activation to reflect the identity of the event.",
        ],
      },
      {
        slug: "pulse",
        label: "Pulse",
        title: "Pulse",
        description: "Trigger a timed, venue-wide smartphone activation where audience screens pulse in brand-defined colors around a central sponsor placement, culminating in a progressive winner reveal that turns collective participation into a high-visibility digital billboard moment.",
        heroTitle: "Pulse",
        heroSubtitle: "by Fanpictor",
        heroDescriptions: [
          "Pulse creates sponsor-owned audience moments by turning participant smartphones into synchronized branded experiences.",
          "Organizations can activate live moments where every participant focuses on the same experience; combining engagement, sponsor visibility, rewards, and measurable interaction in real time.",
        ],
      },
      {
        slug: "mvp-voting",
        label: "MVP Voting",
        title: "MVP Voting",
        heroTitle: "MVP Voting",
        heroSubtitle: "by Fanpictor",
        description: "Let fans vote for the standout player or protagonist after the event or during key performance moments, turning opinion into measurable participation, fan sentiment, and sponsor visibility.",
      },
      {
        slug: "quizzes",
        label: "Quizzes",
        title: "Quizzes",
        description: "Use Fanpictor's quiz engine to engage your audience through a range of interactive quiz formats that boost participation, increase sponsor visibility, and create measurable engagement signals before, during, and after the event.",
      },
      {
        slug: "live-prediction",
        label: "Live Prediction",
        title: "Live Prediction",
        heroTitle: "Live Prediction",
        heroSubtitle: "by Fanpictor",
        description: "Let audiences predict what happens next through live prediction experiences designed to increase participation, extend attention, and keep engagement active across the full fan journey.",
      },
      {
        slug: "polls",
        label: "Polls",
        title: "Polls",
        heroTitle: "Polls",
        heroSubtitle: "by Fanpictor",
        description: "Use live polls to understand what audiences actually prefer, expect, or want next — helping teams, sponsors, and event organizers make decisions based on real participation instead of assumptions.",
      },
      {
        slug: "rookie-of-the-season",
        label: "Rookie of the Season",
        title: "Rookie of the Season",
        heroTitle: "Rookie of the Season",
        heroSubtitle: "by Fanpictor",
        description: "Turn emerging talent into a season-long fan participation journey that increases recurring engagement, repeat voting activity, and sponsor visibility across multiple matchdays.",
      },
      {
        slug: "coolest-scorer",
        label: "Coolest Scorer",
        title: "Coolest Scorer",
        heroTitle: "Coolest Scorer",
        heroSubtitle: "by Fanpictor",
        description: "Turn standout goals and scoring moments into interactive fan voting experiences that extend engagement beyond the final whistle while creating sponsor-owned moments around the plays audiences remember most.",
      },
    ],
  },
  {
    slug: "audience-insights",
    name: "Audience Insights",
    tagline: "Explore how Fanpictor captures audience data and shapes engagement into fan profiles, behavioral insights, and activation opportunities for stronger targeting and engagement strategies.",
    icon: BarChart3,
    submodules: [
      {
        slug: "audience-profile",
        label: "Audience Profile",
        title: "Audience Profile",
        description: "Build rich first-party audience profiles from engagement data, demographics, and behavioural signals across events.",
        heroTitle: "Audience Insights",
        heroSubtitle: "Audience Profile by Fanpictor",
        heroDescriptions: [
          "Audience Profile helps organizations understand who their audience is by capturing structured demographic and profile-based data through interactive experiences.",
          "It turns participation into a clearer view of audience segments, preferences, and commercial opportunities.",
        ],
        demoHeading: "See Audience Profile in action",
        demoDescription: "Explore how audience data can be captured, structured, and visualized through the Fanpictor platform.",
      },
      {
        slug: "engagement-behavior",
        label: "Engagement Behavior",
        title: "Engagement Behavior",
        description: "Analyse how fans interact with activations, measuring participation rates, dwell time, and conversion patterns.",
        heroTitle: "Audience Insights",
        heroSubtitle: "Engagement Behavior by Fanpictor",
        heroDescriptions: [
          "Engagement Behavior shows how audiences interact across games, campaigns, moments, and digital touchpoints.",
          "It helps organizations identify participation patterns, understand what drives interaction, and turn engagement activity into actionable insight.",
        ],
        demoHeading: "See Engagement Behavior in action",
        demoDescription: "Explore how interaction paths and engagement patterns can be tracked and understood across the event lifecycle.",
      },
    ],
  },
  {
    slug: "audience-activation",
    name: "Audience Activation",
    tagline: "See how sponsors, brands, and organizations can activate experiences through rewards, branded moments, placements, and tailored audience journeys.",
    icon: Megaphone,
    submodules: [
      {
        slug: "branded-engagement",
        label: "Audience Activation",
        title: "Audience Activation",
        description: "Create sponsor-owned and campaign-branded audience moments shaped around the identity of a club, event, sponsor, or partner.",
        heroTitle: "Audience Activation",

        heroSubtitle: "Turn participation into sponsor-owned moments",
        heroDescriptions: [
          "Fanpictor lets organizations shape interactive experiences around the identity of a club, event, sponsor, brand, or campaign.",
          "From naming and colors to sponsor visibility and branded feature moments, each activation can become a measurable audience touchpoint.",
        ],
        demoHeading: "A guided walkthrough",
        demoDescription: "See how a branded activation is configured, why it matters commercially, and how it appears to the audience.",
      },
    ],
  },
  {
    slug: "configuration-panel",
    name: "Configuration Panel",
    tagline: "Explore how experiences can be configured through design, content, branding, and interaction settings — giving full control over every activation. ",
    icon: Cog,
    submodules: [
      {
        slug: "design-configuration",
        label: "Design Configuration",
        title: "Design Configuration",
        description: "Customize the look and feel of every activation — colors, logos, layouts — with a visual drag-and-drop builder.",
        heroTitle: "Configuration Panel",
        heroSubtitle: "Design Configuration by Fanpictor",
        heroDescriptions: [
          "Design Configuration gives teams control over the visual setup of Fanpictor experiences.",
          "It allows interactive modules to be adapted to brand, campaign, event, or sponsor requirements while keeping the experience consistent and professional.",
        ],
        demoHeading: "See Design Configuration in action",
        demoDescription: "Explore how visual styling and experience design can be configured across Fanpictor modules.",
      },
      {
        slug: "content-configuration",
        label: "Content Configuration",
        title: "Content Configuration",
        description: "Manage and schedule all event content, media assets, and activation copy from a centralized content hub.",
        heroTitle: "Configuration Panel",
        heroSubtitle: "Content Configuration by Fanpictor",
        heroDescriptions: [
          "Content Configuration allows teams to manage the texts, modules, and interaction logic shown to users.",
          "It helps organizers adapt campaigns, update content, and control the experience without rebuilding the entire setup.",
        ],
        demoHeading: "See Content Configuration in action",
        demoDescription: "Explore how content, labels, and interaction logic can be managed through the Fanpictor configuration panel.",
      },
    ],
  },
];

export function findModule(moduleSlug: string) {
  return MODULES.find((m) => m.slug === moduleSlug);
}

export function findSubmodule(moduleSlug: string, submoduleSlug: string) {
  const mod = findModule(moduleSlug);
  if (!mod) return { mod: undefined, sub: undefined };
  const sub = mod.submodules.find((s) => s.slug === submoduleSlug);
  return { mod, sub };
}
